import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { getCreditReport } from '../../../services/loanService';
import type { DirectorResult } from '../../../services/schemas/loanServiceSchemas';
import CreditDirectorReportsModal from '../../pages/loan-processing/CreditDirectorReportsModal';
import type { DirectorReportView } from '../../pages/loan-processing/creditDirectorReportsTypes';
import { normalizeTaxPinOrIdNumber } from '../../../utils/validation/taxPinAndSouthAfricanId';
import { IndividualIdType, isBureauCheckable, validateIndividualNumber, validatePassportExpiry } from '../../../utils/validation/individualIdentification';

export interface IndividualCreditAssessment {
  idNumber: string;
  idType?: IndividualIdType;
  passportExpiryDate?: string;
  existingCustomer: boolean;
  result: DirectorResult;
}

interface IndividualCreditCheckPanelProps {
  existingCustomer: boolean;
  assessment: IndividualCreditAssessment | null;
  onAssessmentChange: (assessment: IndividualCreditAssessment | null) => void;
}

const formatLabel = (value?: string) =>
  value ? value.replace(/_/g, ' ').toLowerCase().replace(/^./, character => character.toUpperCase()) : 'Not available';

const riskDisplayParts = (result: DirectorResult) => {
  const riskBand = result.scoringEngineResult?.scoring?.risk_band;
  const riskLevel = result.riskLevel ? formatLabel(result.riskLevel) : undefined;
  return { riskBand, riskLevel };
};

const riskBadgeClass = (riskLevel?: string) => {
  switch (riskLevel) {
    case 'LOW':
      return 'bg-success-subtle text-success-emphasis border-success-subtle';
    case 'MEDIUM':
      return 'bg-warning-subtle text-warning-emphasis border-warning-subtle';
    case 'HIGH':
    case 'VERY_HIGH':
      return 'bg-danger-subtle text-danger-emphasis border-danger-subtle';
    default:
      return 'bg-secondary-subtle text-secondary-emphasis border-secondary-subtle';
  }
};

export function IndividualRiskBadge({ result }: { result: DirectorResult }) {
  const { riskBand, riskLevel } = riskDisplayParts(result);
  return (
    <span
      className={`badge rounded-pill border px-3 py-2 ${riskBadgeClass(result.riskLevel)}`}
      aria-label={`Risk profile: ${[riskBand, riskLevel && `${riskLevel} risk`].filter(Boolean).join(', ') || 'Not available'}`}
    >
      {riskBand && <span className="fw-bold">{riskBand}</span>}
      {riskBand && riskLevel && <span className="mx-2" aria-hidden="true">•</span>}
      <span>{riskLevel ? `${riskLevel} risk` : riskBand ? null : 'Not available'}</span>
    </span>
  );
};

const getErrorMessage = (error: unknown) => {
  const candidate = error as { response?: { data?: { message?: string } }; message?: string };
  return candidate?.response?.data?.message || candidate?.message || 'The credit check could not be completed. Please try again.';
};

const toCreditScore = (value?: string): number | null => {
  if (!value?.trim()) return null;
  const score = Number(value);
  return Number.isFinite(score) ? score : null;
};

export default function IndividualCreditCheckPanel({
  existingCustomer,
  assessment,
  onAssessmentChange
}: IndividualCreditCheckPanelProps) {
  const { control, getValues } = useFormContext();
  // Subscribe locally to customer-prefill/reset updates, including values loaded
  // before this step mounts; do not depend on an input edit to refresh the panel.
  const watchedIdNumber = useWatch({
    control,
    name: 'individual.idNumber',
    defaultValue: getValues('individual.idNumber') || ''
  });
  const idType: IndividualIdType = useWatch({control,name:'individual.idType',defaultValue:getValues('individual.idType') || 'SA_ID'});
  const passportExpiryDate = useWatch({control,name:'individual.passportExpiryDate',defaultValue:getValues('individual.passportExpiryDate') || ''});
  const idNumber = idType === 'PASSPORT' ? watchedIdNumber.trim().toUpperCase() : normalizeTaxPinOrIdNumber(watchedIdNumber || '');
  const idIsValid = validateIndividualNumber(idNumber, idType) === true
    && (idType !== 'PASSPORT' || validatePassportExpiry(passportExpiryDate) === true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReport, setShowReport] = useState(false);

  const currentAssessment = assessment && assessment.idNumber === idNumber && (assessment.idType || 'SA_ID') === idType
    && (idType !== 'PASSPORT' || assessment.passportExpiryDate === passportExpiryDate) && assessment.existingCustomer === existingCustomer
    ? assessment
    : null;
  const assessmentComplete = Boolean(currentAssessment?.result.report && currentAssessment.result.scoringEngineResult
    && !currentAssessment.result.failureReason);
  const reportView: DirectorReportView[] = currentAssessment ? [{
    id: currentAssessment.result.directorIdNumber || currentAssessment.idNumber,
    label: currentAssessment.result.directorName || 'Applicant',
    status: currentAssessment.result.failureReason ? 'FAILED' : 'SUCCESS',
    failureReason: currentAssessment.result.failureReason,
    creditScore: toCreditScore(currentAssessment.result.report?.consumerScoring?.finalScore),
    xdsReport: currentAssessment.result.report as unknown as Record<string, unknown> | undefined,
    attributes: currentAssessment.result.attributes,
    scoringEngineResult: currentAssessment.result.scoringEngineResult as unknown as Record<string, unknown> | undefined
  }] : [];

  useEffect(() => {
    if (assessment && (assessment.idNumber !== idNumber || (assessment.idType || 'SA_ID') !== idType
        || (idType === 'PASSPORT' && assessment.passportExpiryDate !== passportExpiryDate) || assessment.existingCustomer !== existingCustomer)) {
      onAssessmentChange(null);
      setError(null);
    }
  }, [assessment, existingCustomer, idNumber, idType, passportExpiryDate, onAssessmentChange]);

  const runCreditCheck = async () => {
    if (!idIsValid || isLoading || !isBureauCheckable(idType)) return;

    setIsLoading(true);
    setError(null);
    onAssessmentChange(null);
    try {
      const identification = idType === 'PASSPORT' ? {idType,passportExpiryDate} : {};
      const result = await getCreditReport({ idNumber, existingCustomer, ...identification });
      if (!result.report && !result.failureReason) {
        setError('The credit check did not return a report. No new assessment result is available. Please retry.');
        return;
      }
      onAssessmentChange({ idNumber, existingCustomer, result, ...identification });
    } catch (requestError) {
      onAssessmentChange(null);
      setError(getErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  };

  // Only an RSA ID or a passport can go to the bureau (owner, 22 Oct 2026): a Customer ID client is historical
  if (!isBureauCheckable(idType)) {
    return (
      <div className="card border-secondary-subtle bg-light mb-3" aria-label="Credit bureau pre-screen">
        <div className="card-body">
          <h6 className="mb-1">Credit bureau pre-screen</h6>
          <p className="small text-muted mb-0">Not available for a Customer ID client: there is no RSA ID or passport on file to send to the credit bureau.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-primary-subtle bg-light mb-3" aria-label="Credit bureau pre-screen">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
          <div>
            <h6 className="mb-1">Credit bureau pre-screen</h6>
            <p className="text-muted small mb-0">
              Run XDS and scoring now, or continue and run it later from Review or Credit Assessment.
            </p>
          </div>
          <span className={`badge ${existingCustomer ? 'bg-info text-dark' : 'bg-secondary'}`}>
            {existingCustomer ? 'Existing customer' : 'New customer'}
          </span>
        </div>

        {!idIsValid && (
          <div className="alert alert-secondary py-2 mt-3 mb-0" role="status">
            {idType === 'PASSPORT' ? 'Enter a valid passport number and expiry date to enable the pre-screen.' : 'Enter a valid South African ID number to enable the pre-screen.'}
          </div>
        )}

        {idIsValid && (
          <>
            <button
              type="button"
              className="btn btn-primary mt-3"
              onClick={runCreditCheck}
              disabled={isLoading}
            >
              {isLoading ? (
                <><span className="spinner-border spinner-border-sm me-2" role="status" />Running XDS and scoring…</>
              ) : currentAssessment ? 'Run XDS and scoring again' : 'Run XDS and scoring'}
            </button>
          </>
        )}

        {error && <div className="alert alert-danger py-2 mt-3 mb-0" role="alert">{error}</div>}

        {currentAssessment && (
          <div className={`alert ${assessmentComplete ? 'alert-success' : 'alert-danger'} mt-3 mb-0`} role="status">
            <div className="fw-semibold mb-2">
              {assessmentComplete ? 'Assessment completed' : 'Assessment requires attention'}
            </div>
            {assessmentComplete && <div className="row g-2">
              <div className="col-sm-6"><strong>Recommendation:</strong> {formatLabel(currentAssessment.result.recommendation)}</div>
              <div className="col-sm-6 d-flex align-items-center gap-2">
                <strong>Risk profile:</strong> <IndividualRiskBadge result={currentAssessment.result} />
              </div>
            </div>}
            {!assessmentComplete && !currentAssessment.result.failureReason && (
              <div>No new scoring result is available for this assessment.</div>
            )}
            {currentAssessment.result.failureReason && (
              <div className="text-danger mt-2"><strong>Assessment issue:</strong> {currentAssessment.result.failureReason}</div>
            )}
            {currentAssessment.result.report && (
              <button
                type="button"
                className="btn btn-link px-0 mt-2"
                onClick={() => setShowReport(true)}
              >
                <i className="fa fa-file-text-o me-2" aria-hidden="true" />
                View XDS report
              </button>
            )}
            <div className="small mt-2">
              This pre-screen is not final approval. Identity, income, affordability and supporting documents still require verification.
            </div>
          </div>
        )}
      </div>
      <CreditDirectorReportsModal
        show={showReport}
        onHide={() => setShowReport(false)}
        reports={reportView}
        selectedIndex={0}
        onSelectIndex={() => undefined}
        processedCount={currentAssessment ? 1 : 0}
        successCount={currentAssessment && !currentAssessment.result.failureReason ? 1 : 0}
        failedCount={currentAssessment?.result.failureReason ? 1 : 0}
        showDownloadControl
        downloadEnabled
      />
    </div>
  );
}
